import hybridApi from './hybridApiClient';
import {
  KnowledgeBaseItem,
  KnowledgeBaseComment,
  KnowledgeBaseViewer,
  UnreadNewsItem,
} from '../types/typesKnowledgeBase';

/** נשלח כשמשתמש פתח פריט — הפעמון מרענן את רשימת החדשות שלא נקראו */
export const KNOWLEDGE_BASE_ITEM_VIEWED_EVENT = 'knowledge-base-item-viewed';

class KnowledgeBaseService {
  async getUnreadNews(): Promise<UnreadNewsItem[]> {
    const response = await hybridApi.get('/api/knowledge-base/news/unread');
    return Array.isArray(response.data) ? (response.data as UnreadNewsItem[]) : [];
  }

  async getAllItems(options?: { softFail?: boolean }): Promise<KnowledgeBaseItem[]> {
    try {
      const response = await hybridApi.get('/api/knowledge-base');
      return Array.isArray(response.data) ? (response.data as KnowledgeBaseItem[]) : [];
    } catch (error: any) {
      const status = Number(error?.response?.status || 0);
      if (options?.softFail && (status >= 500 || status === 404)) return [];
      throw error;
    }
  }

  async getItem(
    id: string,
    viewer?: { userId: string; userName: string; userRole?: string }
  ): Promise<KnowledgeBaseItem | null> {
    try {
      const response = await hybridApi.get(`/api/knowledge-base/${id}`, {
        params: viewer
          ? {
              viewerUserId: viewer.userId,
              viewerUserName: viewer.userName,
              viewerUserRole: viewer.userRole || 'viewer',
            }
          : undefined,
      });
      if (viewer && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(KNOWLEDGE_BASE_ITEM_VIEWED_EVENT, { detail: { id } }));
      }
      return response.data as KnowledgeBaseItem;
    } catch (error: any) {
      if (Number(error?.response?.status || 0) === 404) return null;
      throw error;
    }
  }

  async getItemsByCategory(
    category: string,
    options?: { softFail?: boolean; limit?: number },
  ): Promise<KnowledgeBaseItem[]> {
    const limit = Number(options?.limit);
    const useServerFilter = Number.isFinite(limit) && limit > 0;
    try {
      const response = await hybridApi.get('/api/knowledge-base', {
        params: {
          category: String(category || '').trim() || undefined,
          ...(useServerFilter ? { limit: Math.min(Math.round(limit), 50) } : {}),
        },
      });
      const items = Array.isArray(response.data) ? (response.data as KnowledgeBaseItem[]) : [];
      if (useServerFilter) return items;
      return items.filter((item) => item.category === category);
    } catch (error: any) {
      const status = Number(error?.response?.status || 0);
      if (options?.softFail && (status >= 500 || status === 404)) return [];
      if (status === 404) {
        const items = await this.getAllItems(options);
        return items.filter((item) => item.category === category);
      }
      throw error;
    }
  }

  async createItem(item: Omit<KnowledgeBaseItem, 'id'>): Promise<string> {
    const response = await hybridApi.post('/api/knowledge-base', item);
    return String(response.data?.id || '');
  }

  async updateItem(id: string, updates: Partial<KnowledgeBaseItem>): Promise<void> {
    await hybridApi.patch(`/api/knowledge-base/${id}`, updates);
  }

  async deleteItem(id: string): Promise<void> {
    await hybridApi.delete(`/api/knowledge-base/${id}`);
  }

  async getComments(knowledgeBaseId: string): Promise<KnowledgeBaseComment[]> {
    const response = await hybridApi.get(`/api/knowledge-base/${knowledgeBaseId}/comments`);
    return response.data as KnowledgeBaseComment[];
  }

  async addComment(comment: Omit<KnowledgeBaseComment, 'id'>): Promise<string> {
    const response = await hybridApi.post(`/api/knowledge-base/${comment.knowledgeBaseId}/comments`, comment);
    return String(response.data?.id || '');
  }

  async updateComment(id: string, comment: string): Promise<void> {
    await hybridApi.patch(`/api/knowledge-base/comments/${id}`, { comment });
  }

  async deleteComment(id: string): Promise<void> {
    await hybridApi.delete(`/api/knowledge-base/comments/${id}`);
  }

  async searchItems(searchTerm: string): Promise<KnowledgeBaseItem[]> {
    const items = await this.getAllItems();
    const term = searchTerm.toLowerCase();
    return items.filter((item) =>
      item.title.toLowerCase().includes(term) ||
      item.content.toLowerCase().includes(term) ||
      (item.tags && item.tags.some((tag) => tag.toLowerCase().includes(term)))
    );
  }

  async getItemViewers(knowledgeBaseId: string): Promise<KnowledgeBaseViewer[]> {
    const response = await hybridApi.get(`/api/knowledge-base/${knowledgeBaseId}/viewers`);
    return response.data as KnowledgeBaseViewer[];
  }
}

export const knowledgeBaseService = new KnowledgeBaseService();

